/**
 * CORE-981 — copy resolution for the ended-state hero (title + body).
 *
 * Extracted out of LiveAnnouncementHero's inline ternary so the branch order
 * is testable. The original ternary checked `isOwner` BEFORE `hostNoShow`, so
 * an owner whose broadcast was auto-ended with EndReason=host_no_show fell into
 * the generic «Спасибо за Live-сессию!» + «Запись готовится» copy — thanking
 * them for an event that never happened and promising a recording that will
 * never arrive. `hostNoShow` is now resolved first, with a dedicated,
 * on-brand wording for the host (plain, no false recording promise, and a
 * next step: reschedule or recreate).
 */

import { ownerRecordingCopy, type RecordingOutcome } from '../screens/live/recordingOutcome';

export type EndedVariant =
  | 'completed'
  | 'cancelled'
  | 'subscribers-only'
  | 'attendees-only'
  // CORE-1778 — запись входит в подписку Agapēlla, и у этого человека её нет.
  | 'agapella';

export type EndedHeroCopyInput = {
  isOwner: boolean;
  isRoom: boolean;
  hasRecording: boolean;
  hostNoShow: boolean;
  endedVariant: EndedVariant;
  masterName: string;
  /**
   * CORE-1526 (QA FAIL 05.10) — что случилось с записью. Владельцу без файла
   * герой обещал обработку «обычно 5–15 минут» безусловно: и через месяц
   * после эфира, и когда запись уже сорвалась. Отсутствия ссылки для такого
   * обещания мало — нужен исход.
   *
   * Необязательное: без него владелец видит прежние слова про обработку (так
   * ведут себя вызовы, которые исход ещё не считают).
   */
  recordingOutcome?: RecordingOutcome;
};

export type EndedHeroCopy = { title: string; body: string };

export function resolveEndedHeroCopy(input: EndedHeroCopyInput): EndedHeroCopy {
  const { isOwner, isRoom, hasRecording, hostNoShow, endedVariant } = input;
  const masterName = input.masterName || 'Ведущий';

  const eventGenitive = isRoom ? 'практики' : 'Live-сессии';
  const eventEndedM = isRoom ? 'Практика завершена' : 'Live-сессия завершена';
  const eventCancelled = isRoom ? 'Практика отменена' : 'Live-сессия была отменена';
  const eventNotHappenedShort = isRoom ? 'Практика не состоялась' : 'Live-сессия не состоялась';
  const recordingOfEvent = isRoom ? 'Запись практики' : 'Запись Live-сессии';
  const thanksForEvent = isRoom ? 'Спасибо за практику!' : 'Спасибо за Live-сессию!';

  // host_no_show wins over the generic owner copy: the broadcast never went
  // live, so no recording can exist. `!hasRecording` keeps the (impossible but
  // defensive) recording case falling through to the recording branches below.
  if (hostNoShow && !hasRecording) {
    if (isOwner) {
      return {
        title: eventNotHappenedShort,
        body: `Начать не удалось — подключение не состоялось, поэтому записи не будет. Перенесите встречу или создайте новую, когда будете готовы.`,
      };
    }
    return {
      title: `${masterName} не смог подключиться`,
      body: `${eventNotHappenedShort} в назначенное время. Мы обязательно сообщим, когда ${masterName} запланирует следующую встречу. Спасибо, что присоединились.`,
    };
  }

  const title = isOwner
    ? hasRecording
      ? 'Запись готова'
      : thanksForEvent
    : hasRecording
    ? recordingOfEvent
    : endedVariant === 'cancelled'
    ? eventCancelled
    : endedVariant === 'agapella'
    ? 'Запись входит в Agapēlla'
    : endedVariant === 'subscribers-only'
    ? 'Только для подписчиков'
    : endedVariant === 'attendees-only'
    ? (isRoom ? 'Только для участников практики' : 'Только для участников Live-сессии')
    : eventEndedM;

  // CORE-1526 (QA FAIL 05.10) — обещание обработки только там, где обработка
  // может идти. Исход не передали — остаются прежние слова.
  const ownerNoRecordingBody = input.recordingOutcome && input.recordingOutcome !== 'processing'
    ? ownerRecordingCopy(input.recordingOutcome, { isRoom }).body
    : `${recordingOfEvent} готовится — обычно 5–15 минут.\nСтраница обновится, когда запись будет готова.`;

  const body = isOwner
    ? hasRecording
      ? 'Запись опубликована. Откройте, чтобы пересмотреть\nили поделиться ссылкой с участниками.'
      : ownerNoRecordingBody
    : hasRecording
    ? `${isRoom ? 'Практика завершена' : 'Live-сессия завершена'}, запись доступна для просмотра.`
    : endedVariant === 'cancelled'
    ? `${eventNotHappenedShort}. Загляните в профиль ведущего — возможно, там уже анонсирована новая встреча.`
    : endedVariant === 'agapella'
    ? `Запись этой ${eventGenitive} входит в подписку Agapēlla.`
    : endedVariant === 'subscribers-only'
    ? `Запись этой ${eventGenitive} доступна подписчикам мастера. Оформите подписку, чтобы открыть доступ.`
    : endedVariant === 'attendees-only'
    ? `Запись доступна тем, кто регистрировался на ${isRoom ? 'эту практику' : 'эту Live-сессию'}.`
    : `${isRoom ? 'Практика прошла' : 'Live-сессия прошла'} без записи. Загляните в профиль ведущего — там могут быть другие материалы.`;

  return { title, body };
}
